import firebase from '../../config/firebase';
import { portalVersion } from './helpers';

const TRACKED_KEY = 'portal_version_tracked';

// Compare dotted numeric versions like "2.8.10" vs "2.8.9".
// Returns 1 if a > b, -1 if a < b, 0 if equal. Missing/non-numeric parts sort low.
const compareVersions = (a, b) => {
  const pa = String(a || '')
    .split('.')
    .map((n) => parseInt(n, 10) || 0);
  const pb = String(b || '')
    .split('.')
    .map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const na = pa[i] || 0;
    const nb = pb[i] || 0;
    if (na > nb) return 1;
    if (na < nb) return -1;
  }
  return 0;
};

/**
 * Records the version of the portal a signed-in user is currently running.
 * Writes `lastSeenVersion` and `lastSeenAt` to their user doc once per session
 * per version to avoid excessive Firestore writes.
 *
 * Uses a transaction with a downgrade guard so a stale tab/device still running
 * an OLDER cached bundle can never overwrite a newer `lastSeenVersion` (a plain
 * last-writer-wins `set` made users appear to downgrade). An equal version still
 * refreshes `lastSeenAt`; an older version is skipped entirely.
 */
export const trackUserAppVersion = async (uid) => {
  if (!uid || !portalVersion) return;

  const sessionKey = `${TRACKED_KEY}_${uid}_${portalVersion}`;

  try {
    if (window.sessionStorage.getItem(sessionKey)) return;
  } catch (_) {
    // sessionStorage can be blocked in some contexts; continue optimistically
  }

  try {
    const db = firebase.firestore();
    const userRef = db.collection('users').doc(uid);

    await db.runTransaction(async (tx) => {
      const snap = await tx.get(userRef);
      const stored = snap.exists ? snap.data().lastSeenVersion : null;

      // Never regress: if this bundle is older than what's recorded, skip.
      // (If `stored` is missing or unparseable it sorts low, so we still write.)
      if (stored && compareVersions(portalVersion, stored) < 0) return;

      tx.set(
        userRef,
        {
          lastSeenVersion: portalVersion,
          lastSeenAt: firebase.firestore.FieldValue.serverTimestamp(),
        },
        { merge: true },
      );
    });

    try {
      window.sessionStorage.setItem(sessionKey, Date.now().toString());
    } catch (_) {}
  } catch (error) {
    // Never let version tracking break the app
    console.error('Failed to track user app version:', error);
  }
};
